import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { MARGIN_LABELS } from './paramUtils';

/**
 * Typed editor for the 6-value `Margin` parameter type (stored as a comma string e.g. "5,5,0,5,5,0").
 * Shows 6 labelled numeric fields instead of a raw string, recomposing the string on every change.
 * Same data/pipeline as the plain field — just a friendlier input. Field order = MARGIN_LABELS
 * (confirmed against Unity). Each cell preserves any unit suffix it had (e.g. "30mm").
 */
const PARTS = 6;

interface IMarginCell { num: string; unit: string; }

const parseMargin = (value: string): IMarginCell[] => {
    const parts = (value || '').split(',');
    const cells: IMarginCell[] = [];
    for (let i = 0; i < PARTS; i++) {
        const raw = (parts[i] ?? '').trim();
        const m = raw.match(/^(-?\d*\.?\d*)\s*(.*)$/);
        cells.push({ num: m ? m[1] : '', unit: m ? m[2].trim() : '' });
    }
    return cells;
};

const composeMargin = (cells: IMarginCell[]): string =>
    cells.map(c => `${c.num === '' ? '0' : c.num}${c.unit}`).join(',');

interface IMarginInputProps {
    value: string;
    onChange: (next: string) => void;
}

const MarginInput: React.FC<IMarginInputProps> = ({ value, onChange }) => {
    const cells = parseMargin(value);

    const setCell = (i: number, num: string) => {
        const next = cells.map((c, idx) => (idx === i ? { ...c, num } : c));
        onChange(composeMargin(next));
    };

    return (
        <Box>
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                {cells.map((c, i) => (
                    <TextField
                        key={i}
                        label={MARGIN_LABELS[i] ?? `#${i + 1}`}
                        value={c.num}
                        onChange={(e) => setCell(i, e.target.value)}
                        type="number"
                        size="small"
                        sx={{ width: 76 }}
                    />
                ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Nyers érték: <code>{composeMargin(cells)}</code>
            </Typography>
        </Box>
    );
};

export default MarginInput;
